import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CloseIcon from "../../../../assets/icons/close.svg";
import APIVariables from "../../../../services/api-variables";
import { ReactComponent as Pen } from "../../../../assets/icons/pen.svg";
import {
  handleCatch,
  isEmptyObject,
  isObject,
  validateEmail,
} from "../../../../shared/helpers";
import { App } from "../../../../routes/Constants";
import QuitModal from "../../../common/Modal/QuitModal";
import RemoveNurseModal from "../removeNurseModal";
import LocalStorage from "../../../../shared/localStorage";
import Loader from "../../../common/Loader/Loader";
import { errorMessages } from "../../../../constants/errorMessages.constants";
import { ReactComponent as Undo } from "../../../../assets/icons/undo.svg";
import Axios from "../../../../services/Axios";

const inputStyles =
  "mt-2.5 mb-[15px] w-full border-0 border-b border-[#c8c8c8] bg-[#f8f8f8] px-0 py-1 text-lg leading-[22px] text-[#646464] outline-none disabled:cursor-not-allowed";

const editInputStyles =
  "mt-2.5 mb-[15px] w-full border-0 border-b border-[#c8c8c8] bg-white px-0 py-1 text-lg leading-[22px] text-[#646464] outline-none";

const editErrorInputStyles = `${editInputStyles} error`;

const NurseEdit = () => {
  const navigate = useNavigate();
  const selectedNurse = window.location.pathname.split("/")[2];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nurseForm, setNurseForm] = useState({});
  const [hospitalDetails, setHospitalDetails] = useState();
  const [quitModalIsOpen, setIsOpenQuit] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [modalIsOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [unchangedFields, setUnchangedFields] = useState({});

  const [isLoadingSaveButton, setIsLoadingSaveButton] = useState(false);

  useEffect(() => {
    getData();

    // When destroy profile component, remove IS_EDIT_MODE_NHSPORTAL  from localstorage
    return () => {
      localStorage.removeItem(LocalStorage.IS_EDIT_MODE_NHSPORTAL);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = async () => {
    await Axios.get(
      APIVariables.HCP_CONNECTED_NURSE + window.location.pathname.split("/")[2]
    )
      .then((res) => {
        const { data, status } = res;
        if (status === 200) {
          let nurseForm = {
            ...data,
            ...data.user,
          };
          delete nurseForm.user;
          delete nurseForm.hospital;
          setUnchangedFields(nurseForm);
          setNurseForm(nurseForm);
          setHospitalDetails({ ...data.hospital });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        handleCatch(error);
        setIsLoading(false);
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNurseForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  function closeForm() {
    setIsOpenQuit(true);
  }

  function closeModalQuit() {
    setIsOpenQuit(false);
  }

  function quitModal() {
    navigate(App.DASHBOARD);
  }

  const openModalRemoveNurse = (e) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const removeNurse = async () => {
    setErrorMessage("");

    await Axios.delete(APIVariables.HCP_CONNECTED_NURSES_DELETE + selectedNurse)
      .then((res) => {
        const { status } = res;
        if (status === 200) {
          closeModal();
          navigate(App.NURSES);
        }
      })
      .catch(function (error) {
        const errors = error.response.data;
        if (typeof errors === "string") {
          setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
        } else {
          const errorsArray = [];
          Object.keys(errors).forEach((key) => {
            if (isObject(errors[key])) {
              Object.keys(errors[key]).forEach((secondKey) => {
                errorsArray.push(key + " - " + errors[key][secondKey][0]);
              });
            } else {
              errorsArray.push(key + " - " + errors[key]);
            }
          });
          setErrorMessage(errorsArray);
        }
      });
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveButtonClick = async () => {
    setIsLoadingSaveButton(true);
    await onClickEditSave();
    setIsLoadingSaveButton(false);
  };

  const onClickEditSave = async () => {
    setErrorMessage("");
    setIsSubmitting(false);
    if (isEditMode) {
      localStorage.setItem(LocalStorage.IS_EDIT_MODE_NHSPORTAL, true);
      setIsSubmitting(true);

      if (areAllFields) {
        let buildEditObject = {
          job_title: nurseForm?.job_title,
          user_data: {
            ...nurseForm,
          },
        };

        if (!isEmptyObject(buildEditObject)) {
          const currentNurseID = window.location.pathname.split("/")[2];

          await Axios.put(
            APIVariables.HCP_CONNECTED_NURSES_EDIT + currentNurseID,
            buildEditObject
          )
            .then((res) => {
              const { status } = res;
              if (status === 200) {
                setUnchangedFields(nurseForm);
                setIsEditMode(false);
                localStorage.removeItem(LocalStorage.IS_EDIT_MODE_NHSPORTAL);
              }
            })
            .catch(function (error) {
              handleCatch(error);
              const errors = error?.response?.data;
              const errorsArray = [];
              errorsArray.push(errors);
              setErrorMessage(errorsArray);
            });
        } else {
          setIsEditMode(false);
          localStorage.removeItem(LocalStorage.IS_EDIT_MODE_NHSPORTAL);
        }
      } else {
        localStorage.setItem(LocalStorage.IS_EDIT_MODE_NHSPORTAL, true);
        setIsSubmitting(true);
      }
    } else {
      if (!isEditMode) {
        localStorage.setItem(LocalStorage.IS_EDIT_MODE_NHSPORTAL, true);
      } else {
        localStorage.removeItem(LocalStorage.IS_EDIT_MODE_NHSPORTAL);
      }
      setIsEditMode(!isEditMode);
    }
  };

  const validateForm = ({
    first_name = "",
    last_name = "",
    job_title = "",
    registration_number = "",
    email = "",
  }) => ({
    first_name: {
      isValid: first_name.trim().length > 0,
    },
    last_name: {
      isValid: last_name.trim().length > 0,
    },
    job_title: {
      isValid: job_title.trim().length > 0,
    },
    registration_number: {
      isValid: registration_number.trim().length > 0,
    },
    email: {
      isValid: email.trim().length > 0,
    },
    emailValid: {
      isValid: validateEmail(email),
    },
  });

  const { first_name, last_name, job_title, email, registration_number } =
    nurseForm;

  const validateScreenFields = validateForm({
    first_name,
    last_name,
    job_title,
    email,
    registration_number,
  });

  let areAllFields = Object.values(validateScreenFields).every((field) => {
    return field.isValid;
  });

  const isEmailInvalid = errorMessage && errorMessage[0]?.email;

  const setUndoChanges = () => {
    setNurseForm(unchangedFields);
    setIsEditMode(false);
  };

  const areChanges = () => {
    return JSON.stringify(nurseForm) !== JSON.stringify(unchangedFields);
  };

  if (isLoading) return <Loader />;

  return (
    <div
      className="min-h-screen overflow-y-auto pt-[140px] pb-[100px]"
      data-testid="nurses-edit-page"
    >
      <div className="relative mx-auto max-w-[1100px] rounded-[10px] bg-white px-6 py-5 shadow-[0px_0px_40px_rgba(0,0,0,0.2)] sm:px-10">
        <button
          type="button"
          className="absolute right-[15px] top-[15px] cursor-pointer rounded-full bg-[#9ABDB8] p-[3px]"
          onClick={closeForm}
          aria-label="Close form"
          data-testid="nurses-edit-close-button"
        >
          <img src={CloseIcon} alt="" className="brightness-0 invert" />
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3 pr-10">
          <h1 className="select-category-txt mineShaft-color mt-2.5 pb-1.5">
            {nurseForm.first_name} {nurseForm.last_name} - Registered Nurse
          </h1>
          <div className="flex items-center gap-2 mb-4">
            <button
              type="button"
              className="second-button flex items-center gap-1"
              disabled={isLoadingSaveButton}
              onClick={handleSaveButtonClick}
              data-testid="nurses-edit-save-button"
            >
              {!isEditMode ? "Edit" : "Save"} Profile <Pen />
            </button>
            <button
              type="button"
              className="second-button red-button"
              onClick={(e) => openModalRemoveNurse(e)}
              data-testid="nurses-edit-remove-button"
            >
              Remove
            </button>
          </div>
        </div>
        <hr className="my-0 h-px border-0 bg-[#768692]" />

        <h2 className="title-details mineShaft-color mt-[30px] mb-[25px]">
          Nurse Details
        </h2>

        <div className="mb-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="blue-label" htmlFor="edit-nurse-first-name">
                Nurse First Name:
              </label>
              <input
                id="edit-nurse-first-name"
                type="text"
                onChange={handleChange}
                name="first_name"
                value={nurseForm.first_name}
                maxLength={30}
                className={inputStyles}
                disabled
                data-testid="nurses-edit-first-name-input"
              />
              {isSubmitting && !validateScreenFields.first_name?.isValid && (
                <span className="required-text">Required</span>
              )}
            </div>
            <div>
              <label className="blue-label" htmlFor="edit-nurse-last-name">
                Nurse Last Name:*
              </label>
              <input
                id="edit-nurse-last-name"
                type="text"
                onChange={handleChange}
                name="last_name"
                value={nurseForm.last_name}
                maxLength={30}
                data-testid="nurses-edit-last-name-input"
                className={
                  isEditMode
                    ? isSubmitting && !validateScreenFields.last_name.isValid
                      ? editErrorInputStyles
                      : editInputStyles
                    : inputStyles
                }
                disabled={!isEditMode}
              />
              {isSubmitting && !validateScreenFields.last_name?.isValid && (
                <span className="required-text">Required</span>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="blue-label" htmlFor="edit-nurse-reg-number">
                Registration number:
              </label>
              <input
                id="edit-nurse-reg-number"
                type="text"
                onChange={handleChange}
                name="registration_number"
                value={nurseForm.registration_number || ""}
                maxLength={30}
                disabled
                className={inputStyles}
                data-testid="nurses-edit-registration-input"
              />
            </div>
            <div>
              <label className="blue-label" htmlFor="edit-nurse-job-title">
                Job Title:*
              </label>
              <input
                id="edit-nurse-job-title"
                type="text"
                onChange={handleChange}
                name="job_title"
                value={nurseForm.job_title}
                maxLength={30}
                data-testid="nurses-edit-job-title-input"
                className={
                  isEditMode
                    ? isSubmitting && !validateScreenFields.job_title.isValid
                      ? editErrorInputStyles
                      : editInputStyles
                    : inputStyles
                }
                disabled={!isEditMode}
              />
              {isSubmitting && !validateScreenFields.job_title?.isValid && (
                <span className="required-text">Required</span>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="blue-label" htmlFor="edit-nurse-hospital-name">
                Hospital Name:
              </label>
              <input
                id="edit-nurse-hospital-name"
                type="text"
                name="hospital"
                value={hospitalDetails?.name}
                disabled
                className={inputStyles}
                data-testid="nurses-edit-hospital-name-input"
              />
            </div>
            <div>
              <label className="blue-label" htmlFor="edit-nurse-email">
                Email Address:
              </label>
              <input
                id="edit-nurse-email"
                type="text"
                onChange={handleChange}
                name="email"
                value={nurseForm.email}
                maxLength={50}
                className={inputStyles}
                disabled
                data-testid="nurses-edit-email-input"
              />
              {isSubmitting && !validateScreenFields.email?.isValid && (
                <span className="required-text">Required</span>
              )}
              {isSubmitting &&
                !validateScreenFields.emailValid.isValid &&
                validateScreenFields.email.isValid && (
                  <span className="required-text">Invalid Email</span>
                )}
              {isEmailInvalid && <span className="required-text">{isEmailInvalid}</span>}
            </div>
          </div>

          <div className="mt-4">
            <label className="blue-label" htmlFor="edit-nurse-hospital-address">
              Hospital Address:
            </label>
            <input
              id="edit-nurse-hospital-address"
              type="text"
              name="hospital"
              value={hospitalDetails?.address.full_address}
              disabled
              className={inputStyles}
              data-testid="nurses-edit-hospital-address-input"
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          {areChanges() ? (
            <button
              type="button"
              className="flex items-center gap-1 border-0 bg-transparent font-size-18 text-[#00567a] hover:text-[#8c8c8c]"
              onClick={() => setUndoChanges()}
              data-testid="nurses-edit-undo-button"
            >
              <Undo /> Undo Changes
            </button>
          ) : null}
        </div>
      </div>

      {quitModalIsOpen && (
        <QuitModal
          quitModalIsOpen={quitModalIsOpen}
          closeModalQuit={closeModalQuit}
          quitModal={quitModal}
          description="Are you sure you want to cancel filling out this form? All progress will be lost and you will need to start again."
        />
      )}

      {modalIsOpen && (
        <RemoveNurseModal
          modalIsOpen={modalIsOpen}
          closeModal={closeModal}
          removeNurse={removeNurse}
          errorMessage={errorMessage}
        />
      )}
    </div>
  );
};
export default NurseEdit;
